import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  List,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';

import NotificationHeader from '../components/NotificationHeader';
import NotificationItem from '../components/NotificationItem.js';
import EmptyState from '../components/EmptyState.js';
import { apiService } from '../components/ReasuableFuncations/api';

const NotificationPopup = ({
  setPopupOpen,
  upcomingProjects = [],
  filteredRisks = [],
  filteredReminders = [],
  newsAlerts = [],
  eventAlerts = [],
  updateNotificationCount,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Add safety checks before mapping
  const safeProjects = useMemo(() => Array.isArray(upcomingProjects) ? upcomingProjects : [], [upcomingProjects]);
  const safeRisks = useMemo(() => Array.isArray(filteredRisks) ? filteredRisks : [], [filteredRisks]);
  const safeReminders = useMemo(() => Array.isArray(filteredReminders) ? filteredReminders : [], [filteredReminders]);

  useEffect(() => {
    // Combine and format all notifications
    const allNotifications = [
      ...safeProjects.map(p => ({ ...p, type: 'project' })),
      ...safeRisks.map(r => ({ ...r, type: 'risk' })),
      ...safeReminders.map(r => ({ ...r, type: 'reminder' })),
      ...newsAlerts.map(n => ({ ...n, type: 'news' })),
      ...eventAlerts.map(e => ({ ...e, type: 'event' }))
    ].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.date);
      const dateB = new Date(b.createdAt || b.date);
      return dateB.getTime() - dateA.getTime();
    });

    setNotifications(allNotifications);
  }, [safeProjects, safeRisks, safeReminders, newsAlerts, eventAlerts]);

  const handleMarkAsRead = async (notification) => {
    setLoading(true);
    try {
      const response = await apiService.patch(`/api/${notification._id}/read?name=${notification.type}`);

      if (response.statusText !== 'OK') {
        throw new Error('Failed to update notification');
      }

      // Update notifications state immediately
      setNotifications(prev => prev.map(n => 
        n._id === notification._id 
          ? { ...n, isRead: true }
          : n
      ));

      // Update the notification count
      updateNotificationCount && updateNotificationCount(prev => Math.max(0, prev - 1));

      // Navigate without timeout
      const routes = {
        project: '/project-list',
        risk: '/risk-dashboard',
        reminder: '/',
        news: '/new-event',
        event: '/new-event'
      };
      navigate(routes[notification.type]);
      setPopupOpen(false);

    } catch (err) {
      console.error('Error:', err);
      setError('Failed to mark notification as read');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => setPopupOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <NotificationHeader 
        notificationCount={notifications.filter(n => !n.isRead).length}
        onClose={() => setPopupOpen(false)}
      />

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {notifications.length === 0 && !loading && <EmptyState />}

        <List sx={{ p: 0 }}>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification._id}>
              <NotificationItem
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
              />
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError('')} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

NotificationPopup.propTypes = {
  setPopupOpen: PropTypes.func.isRequired,
  upcomingProjects: PropTypes.arrayOf(PropTypes.object),
  filteredRisks: PropTypes.arrayOf(PropTypes.object),
  filteredReminders: PropTypes.arrayOf(PropTypes.object),
  newsAlerts: PropTypes.arrayOf(PropTypes.object),
  eventAlerts: PropTypes.arrayOf(PropTypes.object),
  updateNotificationCount: PropTypes.func
};

export default NotificationPopup;